import React, { useEffect, useRef } from 'react';

import { InformedFileUploaderField } from '@nw/shared/ui';
import { getS3FileNameWithPrefix } from '@nw/shared/utils';

import {
  FileUploaderMachineActor,
  useFileUploader,
  FileUploaderProvider
} from '../../../../data-access/src';

import { fileUploaderFieldContainerStyles } from './styledComponents';

interface InformedFileUploaderFieldProps {
  field: string;
  label: string;
  error: any;
  touched: any;
  validationSchemaObject: any;
  fileName: string;
  overrideBucketPath?: string;
  isDisabled?: boolean;
  shouldShowDeleteButton?: boolean;
  labelTextCSS?: React.CSSProperties;
  aclType?: string;
  styledHintClassName?: string;
  getS3FileUploaderUrl?: (url: string) => string;
  fieldContainerStyles?: React.CSSProperties;
  onFileUpload?: (file: string) => void;
  fileUploaderActorRef?: React.MutableRefObject<FileUploaderMachineActor | null>;
  s3UploadBaseUrl?: string;
  refreshAuthTokensHook?: () => {
    refreshTokenApi: any;
    getAccessToken: () => string;
    getRefreshToken: () => string;
  };
}

const InformedFileUploaderFieldComponentInner = (
  props: InformedFileUploaderFieldProps
) => {
  const {
    field,
    error,
    touched,
    label,
    fileName,
    isDisabled,
    validationSchemaObject,
    overrideBucketPath,
    shouldShowDeleteButton,
    labelTextCSS,
    aclType,
    styledHintClassName,
    getS3FileUploaderUrl,
    fieldContainerStyles,
    onFileUpload,
    fileUploaderActorRef
  } = props;

  const { fileUploadersMachine, getAwsCredentials } = useFileUploader();

  const [state, send] = fileUploadersMachine;

  useEffect(() => {
    if (true && !state?.context?.configuredAWSS3Client) {
      getAwsCredentials && getAwsCredentials();
    }
  }, []);

  const fileUploaderField = state?.context?.fileUploaders?.find(
    (uploader) => uploader.machine.id === field
  );

  const fileUploaderFieldRef = useRef<FileUploaderMachineActor | undefined>(
    fileUploaderField
  );

  useEffect(() => {
    if (state?.context?.configuredAWSS3Client) {
      if (!fileUploaderFieldRef.current) {
        send({
          type: 'SPAWN_NEW_FILE_UPLOADER',
          id: field,
          assignActor: (actor: any) => (fileUploaderFieldRef.current = actor),
          aclType: aclType,
          getS3FileUploaderUrl: getS3FileUploaderUrl
        });
      }
    }
  }, [state]);

  useEffect(() => {
    if (fileUploaderActorRef) {
      fileUploaderActorRef.current = fileUploaderFieldRef.current ?? null;
    }
  }, [fileUploaderActorRef, state]);

  return (
    fileUploaderFieldRef.current && (
      <InformedFileUploaderField
        field={field}
        error={error}
        touched={touched}
        label={label}
        validationSchema={validationSchemaObject}
        fileUploader={fileUploaderFieldRef.current}
        overrideBucketPath={overrideBucketPath}
        getCustomFileName={getS3FileNameWithPrefix(fileName)}
        fieldContainerStyles={
          fieldContainerStyles ?? fileUploaderFieldContainerStyles
        }
        isDisabled={isDisabled}
        isHidden={false}
        shouldShowDeleteButton={shouldShowDeleteButton}
        labelTextCSS={labelTextCSS}
        styledHintClassName={styledHintClassName}
        onFileUpload={onFileUpload}
      />
    )
  );
};

export const InformedFileUploaderFieldComponent = (
  props: InformedFileUploaderFieldProps
) => {
  const { s3UploadBaseUrl, refreshAuthTokensHook, ...restProps } = props;

  if (s3UploadBaseUrl) {
    return (
      <FileUploaderProvider
        s3UploadBaseUrl={s3UploadBaseUrl}
        refreshAuthTokensHook={refreshAuthTokensHook}
      >
        <InformedFileUploaderFieldComponentInner
          {...(restProps as InformedFileUploaderFieldProps)}
        />
      </FileUploaderProvider>
    );
  }

  return (
    <InformedFileUploaderFieldComponentInner
      {...(restProps as InformedFileUploaderFieldProps)}
    />
  );
};
